import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip, Pencil, X, FileUp, Loader2 } from "lucide-react";
import { API, inputCls, selectCls, eur, dstr, btnGhost, btnRed } from "./shared";

const CATEGORIES = {
  einnahme: ["Rechnung", "Anzahlung", "Sonstige Einnahme"],
  ausgabe: ["Material", "Werkzeug & Maschinen", "Fahrzeug & Sprit", "Büro & Verwaltung", "Versicherung", "Miete", "Marketing", "Sonstige Ausgabe"],
};

const emptyForm = () => ({
  kind: "ausgabe",
  date: new Date().toISOString().slice(0, 10),
  description: "",
  vendor: "",
  doc_number: "",
  category: "Material",
  amount: "",
  vat_rate: 19,
  invoice_id: "",
});

export const Transactions = ({ onChanged }) => {
  const [txs, setTxs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const emptyImport = () => ({
    kind: "einnahme",
    partner: "",
    doc_number: "",
    date: new Date().toISOString().slice(0, 10),
    category: "Rechnung",
    amount: "",
    vat_rate: 19,
    file: null,
  });
  const [imp, setImp] = useState(emptyImport());
  const fileRef = useRef(null);
  const attachTxId = useRef("");
  const pdfRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/transactions`, { withCredentials: true }).then(({ data }) => setTxs(data)).catch(() => {});
    axios.get(`${API}/invoices`, { withCredentials: true }).then(({ data }) => setInvoices(data)).catch(() => {});
    axios.get(`${API}/receipts`, { withCredentials: true }).then(({ data }) => setReceipts(data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.description.trim() || !form.date) {
      toast.error("Bitte Datum und Beschreibung angeben.");
      return;
    }
    const payload = { ...form, amount: parseFloat(String(form.amount).replace(",", ".")) || 0, vat_rate: parseInt(form.vat_rate) || 0 };
    try {
      if (editId) {
        await axios.patch(`${API}/transactions/${editId}`, payload, { withCredentials: true });
        toast.success("Buchung aktualisiert.");
      } else {
        await axios.post(`${API}/transactions`, payload, { withCredentials: true });
        toast.success("Buchung gespeichert.");
      }
      setForm(emptyForm());
      setEditId(null);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen.");
    }
  };

  const edit = (t) => {
    setEditId(t.id);
    setForm({ kind: t.kind, date: t.date, description: t.description, vendor: t.vendor || "", doc_number: t.doc_number || "", category: t.category, amount: t.amount, vat_rate: t.vat_rate, invoice_id: t.invoice_id || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    await axios.delete(`${API}/transactions/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setTxs((xs) => xs.filter((x) => x.id !== id));
    onChanged?.();
  };

  const attach = (txId) => {
    attachTxId.current = txId;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("transaction_id", attachTxId.current);
    try {
      await axios.post(`${API}/receipts`, fd, { withCredentials: true });
      toast.success("Beleg hochgeladen und zugeordnet.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen.");
    }
  };

  const shown = txs.filter((t) => filter === "alle" || t.kind === filter);
  const receiptCount = (id) => receipts.filter((r) => r.transaction_id === id).length;

  const setImpField = (k) => (e) => setImp({ ...imp, [k]: e.target.value });

  const importInvoice = async () => {
    if (!imp.partner.trim() || !imp.doc_number.trim() || !imp.file) {
      toast.error("Bitte Kunde/Lieferant, Rechnungsnummer und PDF-Datei angeben.");
      return;
    }
    setImporting(true);
    try {
      const kindLabel = imp.kind === "einnahme" ? "Ausgangsrechnung" : "Eingangsrechnung";
      const { data: tx } = await axios.post(`${API}/transactions`, {
        kind: imp.kind,
        date: imp.date,
        description: `${kindLabel} ${imp.doc_number} – ${imp.partner}`,
        vendor: imp.partner,
        doc_number: imp.doc_number,
        category: imp.category,
        amount: parseFloat(String(imp.amount).replace(",", ".")) || 0,
        vat_rate: parseInt(imp.vat_rate) || 0,
      }, { withCredentials: true });
      const fd = new FormData();
      fd.append("file", imp.file);
      fd.append("transaction_id", tx.id);
      fd.append("doc_number", imp.doc_number);
      fd.append("vendor", imp.partner);
      fd.append("kind_label", kindLabel);
      await axios.post(`${API}/receipts`, fd, { withCredentials: true });
      toast.success(`${kindLabel} ${imp.doc_number} importiert – zählt ab sofort im Diagramm und in den Exporten mit.`);
      setImp(emptyImport());
      setShowImport(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import fehlgeschlagen.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="transactions-view">
      <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" data-testid="tx-file-input" onChange={onFile} />

      {/* Alte Rechnungen als PDF importieren */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button data-testid="tx-import-toggle" onClick={() => setShowImport(!showImport)} className={btnGhost}>
          <FileUp size={14} /> Alte Rechnung importieren (PDF)
        </button>
      </div>
      {showImport && (
        <div className="bg-[#1e2226] border border-amber-400/30 rounded-2xl p-6 mb-6" data-testid="tx-import-form">
          <p className="font-display text-amber-300/90 text-xs uppercase tracking-[0.2em] mb-2">Rechnung aus altem Programm importieren</p>
          <p className="text-white/50 text-xs mb-5">PDF der alten Rechnung hochladen und die Daten eintragen – sie erscheint in der Liste, im Diagramm und in beiden Exporten. Deine RE-Nummerierung bleibt unberührt.</p>
          <div className="flex gap-3 mb-4">
            {[["einnahme", "Ausgangsrechnung (an Kunden)"], ["ausgabe", "Eingangsrechnung (von Lieferant)"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`import-kind-${k}`}
                onClick={() => setImp({ ...imp, kind: k, category: k === "einnahme" ? "Rechnung" : "Material" })}
                className={`px-4 py-2.5 rounded-full border font-display text-xs font-medium transition-colors ${
                  imp.kind === k
                    ? k === "einnahme" ? "bg-emerald-500 border-emerald-500 text-white" : "bg-[#e2001a] border-[#e2001a] text-white"
                    : "border-white/20 text-white/60 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-white/40 text-xs">{imp.kind === "einnahme" ? "Kunde *" : "Lieferant *"}</label>
              <input data-testid="import-partner" value={imp.partner} onChange={setImpField("partner")} className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-white/40 text-xs">Alte Rechnungsnr. *</label>
              <input data-testid="import-doc-number" value={imp.doc_number} onChange={setImpField("doc_number")} placeholder="z. B. 2024-0157" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-white/40 text-xs">Rechnungsdatum</label>
              <input data-testid="import-date" type="date" value={imp.date} onChange={setImpField("date")} className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-white/40 text-xs">Kategorie</label>
              <select data-testid="import-category" value={imp.category} onChange={setImpField("category")} className={`${selectCls} mt-1`}>
                {CATEGORIES[imp.kind].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-white/40 text-xs">Betrag netto € *</label>
              <input data-testid="import-amount" type="number" min="0" step="0.01" value={imp.amount} onChange={setImpField("amount")} placeholder="0,00" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-white/40 text-xs">MwSt.-Satz</label>
              <select data-testid="import-vat" value={imp.vat_rate} onChange={setImpField("vat_rate")} className={`${selectCls} mt-1`}>
                <option value={19}>19 % MwSt.</option>
                <option value={7}>7 % MwSt.</option>
                <option value={0}>0 % (steuerfrei)</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <input ref={pdfRef} type="file" accept=".pdf,image/*" className="hidden" data-testid="import-file-input" onChange={(e) => setImp({ ...imp, file: e.target.files?.[0] || null })} />
            <button data-testid="import-file-pick" onClick={() => pdfRef.current?.click()} className={btnGhost}>
              <FileUp size={14} /> {imp.file ? imp.file.name : "PDF / Scan wählen *"}
            </button>
            <button data-testid="import-submit" onClick={importInvoice} disabled={importing} className={`${btnRed} disabled:opacity-50`}>
              {importing ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Rechnung importieren
            </button>
          </div>
        </div>
      )}

      {/* Formular */}
      <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6" data-testid="tx-form">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em]">{editId ? "Buchung bearbeiten" : "Neue Buchung"}</p>
          {editId && (
            <button data-testid="tx-cancel-edit" onClick={() => { setEditId(null); setForm(emptyForm()); }} className="text-white/40 hover:text-white text-xs inline-flex items-center gap-1">
              <X size={13} /> Abbrechen
            </button>
          )}
        </div>
        <div className="flex gap-3 mb-4">
          {[["einnahme", "Ausgangsrechnung (Einnahme)"], ["ausgabe", "Eingangsrechnung (Ausgabe)"]].map(([k, label]) => (
            <button
              key={k}
              data-testid={`tx-kind-${k}`}
              onClick={() => setForm({ ...form, kind: k, category: CATEGORIES[k][0], invoice_id: k === "ausgabe" ? "" : form.invoice_id, vendor: k === "einnahme" ? "" : form.vendor })}
              className={`px-5 py-2.5 rounded-full border font-display text-sm font-medium transition-colors ${
                form.kind === k
                  ? k === "einnahme" ? "bg-emerald-500 border-emerald-500 text-white" : "bg-[#e2001a] border-[#e2001a] text-white"
                  : "border-white/20 text-white/60 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-white/40 text-xs">Rechnungsdatum *</label>
            <input data-testid="tx-date" type="date" value={form.date} onChange={set("date")} className={`${inputCls} mt-1`} />
          </div>
          {form.kind === "ausgabe" && (
            <div>
              <label className="text-white/40 text-xs">Lieferant</label>
              <input data-testid="tx-vendor" value={form.vendor} onChange={set("vendor")} placeholder="z. B. EKO-OKNA, Bauhaus" className={`${inputCls} mt-1`} />
            </div>
          )}
          <div>
            <label className="text-white/40 text-xs">Rechnungsnr. des Belegs</label>
            <input data-testid="tx-doc-number" value={form.doc_number} onChange={set("doc_number")} placeholder="z. B. 2026-4571" className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-white/40 text-xs">Beschreibung *</label>
            <input data-testid="tx-description" value={form.description} onChange={set("description")} placeholder={form.kind === "ausgabe" ? "z. B. Fenster für Auftrag Müller" : "z. B. Anzahlung Auftrag Müller"} className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-white/40 text-xs">Kategorie</label>
            <select data-testid="tx-category" value={form.category} onChange={set("category")} className={`${selectCls} mt-1`}>
              {CATEGORIES[form.kind].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-white/40 text-xs">Betrag netto € *</label>
            <input data-testid="tx-amount" type="number" min="0" step="0.01" value={form.amount} onChange={set("amount")} placeholder="0,00" className={`${inputCls} mt-1`} />
          </div>
          <div>
            <label className="text-white/40 text-xs">MwSt.-Satz</label>
            <select data-testid="tx-vat" value={form.vat_rate} onChange={set("vat")} className={`${selectCls} mt-1`}>
              <option value={19}>19 % MwSt.</option>
              <option value={7}>7 % MwSt.</option>
              <option value={0}>0 % (steuerfrei)</option>
            </select>
          </div>
          {form.kind === "einnahme" && (
            <div>
              <label className="text-white/40 text-xs">Deine Rechnung (optional)</label>
              <select data-testid="tx-invoice" value={form.invoice_id} onChange={set("invoice_id")} className={`${selectCls} mt-1`}>
                <option value="">Rechnung zuordnen …</option>
                {invoices.map((i) => <option key={i.id} value={i.id}>{i.number} · {i.customer_name}</option>)}
              </select>
            </div>
          )}
        </div>
        <button data-testid="tx-save" onClick={save} className={`${btnRed} mt-5`}>
          <Plus size={15} /> {editId ? "Änderungen speichern" : "Buchung speichern"}
        </button>
      </div>

      {/* Filter + Liste */}
      <div className="flex gap-2 mb-4">
        {[["alle", "Alle"], ["einnahme", "Einnahmen"], ["ausgabe", "Ausgaben"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`tx-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-4 py-2 rounded-full border font-display text-xs font-medium transition-colors ${
              filter === k ? "bg-[#e2001a] border-[#e2001a] text-white" : "border-white/20 text-white/55 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div data-testid="tx-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
          Noch keine Buchungen – erfasse oben die erste Einnahme oder Ausgabe.
        </div>
      ) : (
        <div data-testid="tx-list" className="space-y-2.5">
          {shown.map((t) => (
            <div key={t.id} data-testid={`tx-${t.id}`} className="bg-[#1e2226] border border-white/10 rounded-xl px-5 py-4 flex flex-wrap items-center gap-3">
              <span className={`text-[10px] font-medium uppercase tracking-widest border rounded-full px-3 py-1 ${
                t.kind === "einnahme" ? "border-emerald-400/50 text-emerald-300" : "border-[#ff4d5e]/50 text-[#ff8090]"
              }`}>
                {t.kind === "einnahme" ? "Einnahme" : "Ausgabe"}
              </span>
              <div className="flex-1 min-w-[200px]">
                <p className="font-display font-medium text-sm">{t.description}</p>
                <p className="text-white/40 text-xs mt-0.5">
                  {dstr(t.date)}
                  {t.vendor && ` · ${t.vendor}`}
                  {t.doc_number && ` · Nr. ${t.doc_number}`}
                  {t.category && ` · ${t.category}`}
                  {t.invoice_id && ` · ${invoices.find((i) => i.id === t.invoice_id)?.number || "Rechnung"}`}
                </p>
              </div>
              <div className="text-right">
                <p className={`font-display font-bold ${t.kind === "einnahme" ? "text-emerald-300" : "text-[#ff8090]"}`}>
                  {t.kind === "einnahme" ? "+" : "−"}{eur(t.amount)}
                </p>
                <p className="text-white/35 text-[11px]">brutto {eur(t.amount * (1 + (t.vat_rate || 0) / 100))}</p>
              </div>
              <div className="flex gap-1.5">
                <button data-testid={`tx-attach-${t.id}`} onClick={() => attach(t.id)} className={`${btnGhost} !px-3 !py-2`} title="Beleg anhängen">
                  <Paperclip size={13} /> {receiptCount(t.id) > 0 && <span className="text-xs">{receiptCount(t.id)}</span>}
                </button>
                <button data-testid={`tx-edit-${t.id}`} onClick={() => edit(t)} className={`${btnGhost} !px-3 !py-2`} title="Bearbeiten">
                  <Pencil size={13} />
                </button>
                <button data-testid={`tx-delete-${t.id}`} onClick={() => del(t.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Buchung löschen">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
